// Compact ECharts option builders for the dashboard.
// Shared palette + tight spacing + scrollable legends so charts stay readable
// with many categories/series.

import { CHART_PALETTE } from '../../utils/theme';

const textColor = isDark => (isDark ? '#cbd5e1' : '#334155');
const mutedColor = isDark => (isDark ? '#94a3b8' : '#64748b');
const gridColor = isDark =>
  isDark ? 'rgba(148,163,184,0.16)' : 'rgba(100,116,139,0.16)';

// Hex -> rgba() so area gradients can fade the series colour.
const toRgba = (hex, alpha) => {
  const h = String(hex || '').replace('#', '');
  if (h.length !== 6) return hex;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

const tooltipStyle = isDark => ({
  backgroundColor: isDark ? 'rgba(17,24,39,0.96)' : 'rgba(255,255,255,0.98)',
  borderColor: isDark ? '#374151' : '#e5e7eb',
  borderWidth: 1,
  textStyle: {color: isDark ? '#e5e7eb' : '#111827', fontSize: 11},
  extraCssText: 'box-shadow:0 4px 14px rgba(0,0,0,0.15);border-radius:8px;',
});

const legend = isDark => ({
  type: 'scroll',
  bottom: 0,
  left: 'center',
  icon: 'circle',
  itemWidth: 8,
  itemHeight: 8,
  itemGap: 8,
  textStyle: {color: textColor(isDark), fontSize: 10},
  pageIconColor: mutedColor(isDark),
  pageIconInactiveColor: gridColor(isDark),
  pageTextStyle: {color: mutedColor(isDark), fontSize: 9},
  pageIconSize: 9,
});

const axisCommon = isDark => ({
  axisLine: {lineStyle: {color: gridColor(isDark)}},
  axisTick: {show: false},
  axisLabel: {color: mutedColor(isDark), fontSize: 10},
  splitLine: {lineStyle: {color: gridColor(isDark)}},
});

const zoomFor = (isDark, dense) =>
  dense
    ? [
        {type: 'inside'},
        {
          type: 'slider',
          height: 12,
          bottom: 2,
          handleSize: 0,
          borderColor: 'transparent',
          fillerColor: 'rgba(99,102,241,0.15)',
          textStyle: {color: mutedColor(isDark), fontSize: 9},
        },
      ]
    : undefined;

export const lineOption = ({labels = [], series = [], isDark = false}) => {
  const dense = labels.length > 14;
  const opt = {
    color: series.map(s => s.color).filter(Boolean),
    tooltip: {
      trigger: 'axis',
      axisPointer: {type: 'line'},
      ...tooltipStyle(isDark),
    },
    legend: series.length > 1 ? legend(isDark) : {show: false},
    grid: {
      left: 6,
      right: 12,
      top: 14,
      bottom: series.length > 1 ? 30 : dense ? 34 : 12,
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: labels,
      ...axisCommon(isDark),
      axisLine: {show: false},
      axisLabel: {
        color: mutedColor(isDark),
        fontSize: 10,
        rotate: dense ? 35 : 0,
        hideOverlap: true,
      },
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      ...axisCommon(isDark),
      splitLine: {lineStyle: {color: gridColor(isDark)}},
    },
    series: series.map(s => ({
      name: s.name,
      type: 'line',
      data: s.data,
      smooth: 0.3,
      showSymbol: !dense,
      symbolSize: 5,
      lineStyle: {width: 2, color: s.color},
      itemStyle: {color: s.color},
      areaStyle: s.fill === false ? undefined : {opacity: 0.12, color: s.color},
      emphasis: {focus: 'series'},
      sampling: dense ? 'lttb' : undefined,
      large: dense,
    })),
  };
  if (dense) opt.dataZoom = zoomFor(isDark, true);
  return opt;
};

export const barOption = ({
  labels = [],
  series = [],
  isDark = false,
  horizontal = false,
  tooltipFormatter = null,
}) => {
  const dense = labels.length > 12;
  const categoryAxis = {
    type: 'category',
    data: labels,
    ...axisCommon(isDark),
    axisLabel: {
      color: mutedColor(isDark),
      fontSize: 10,
      rotate: !horizontal && dense ? 40 : 0,
      hideOverlap: true,
    },
  };
  const valueAxis = {type: 'value', minInterval: 1, ...axisCommon(isDark)};
  const opt = {
    color: series.map(s => s.color).filter(Boolean),
    tooltip: {
      trigger: 'axis',
      ...(tooltipFormatter ? {formatter: tooltipFormatter} : {}),
      ...tooltipStyle(isDark),
    },
    legend: series.length > 1 ? legend(isDark) : {show: false},
    grid: {
      left: 6,
      right: 12,
      top: 14,
      bottom: !horizontal && dense ? 40 : 22,
      containLabel: true,
    },
    xAxis: horizontal ? valueAxis : categoryAxis,
    yAxis: horizontal ? {...categoryAxis, inverse: true} : valueAxis,
    series: series.map(s => ({
      name: s.name,
      type: 'bar',
      stack: s.stack,
      data: s.data,
      barMaxWidth: 30,
      itemStyle: {
        color: s.color,
        borderRadius: s.stack ? 0 : horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0],
      },
      emphasis: {focus: 'series'},
    })),
  };
  if (dense) {
    opt.dataZoom = horizontal
      ? [
          {type: 'inside', yAxisIndex: 0},
          {
            type: 'slider',
            yAxisIndex: 0,
            width: 12,
            right: 2,
            handleSize: 0,
            borderColor: 'transparent',
            fillerColor: 'rgba(99,102,241,0.15)',
            textStyle: {color: mutedColor(isDark), fontSize: 9},
          },
        ]
      : zoomFor(isDark, true);
  }
  return opt;
};

// Renders every slice by default (maxSlices <= 0 disables the "Other" merge).
// Pass a positive maxSlices to aggregate the tail into "Other".
export const pieOption = ({
  data = [],
  isDark = false,
  donut = true,
  maxSlices = 0,
  showLabels = false,
  centerLabel = null,
  tooltipFormatter = null,
}) => {
  const cleaned = (data || []).filter(
    d => d && d.name != null && Number(d.value) > 0,
  );

  if (cleaned.length === 0) {
    return {
      tooltip: {show: false},
      legend: {show: false},
      series: [
        {
          type: 'pie',
          radius: donut ? ['48%', '72%'] : ['0%', '72%'],
          center: ['50%', '48%'],
          silent: true,
          label: {
            show: true,
            position: 'center',
            color: mutedColor(isDark),
            fontSize: 11,
            formatter: 'No data',
          },
          labelLine: {show: false},
          data: [
            {
              value: 1,
              itemStyle: {color: isDark ? '#374151' : '#e5e7eb'},
            },
          ],
        },
      ],
    };
  }

  const sorted = [...cleaned].sort((a, b) => b.value - a.value);
  let slices = sorted;
  // maxSlices <= 0 means "render every slice" (no Other bucket).
  if (maxSlices > 0 && sorted.length > maxSlices) {
    const head = sorted.slice(0, maxSlices - 1);
    const otherTotal = sorted
      .slice(maxSlices - 1)
      .reduce((sum, d) => sum + Number(d.value), 0);
    slices = [...head, {name: 'Other', value: otherTotal}];
  }
  slices = slices.map(d =>
    d.color ? {name: d.name, value: d.value, itemStyle: {color: d.color}} : d,
  );

  const total = slices.reduce((sum, d) => sum + Number(d.value), 0);

  const opt = {
    color: CHART_PALETTE,
    tooltip: {
      trigger: 'item',
      formatter: tooltipFormatter || '{b}: {c} ({d}%)',
      ...tooltipStyle(isDark),
    },
    legend: legend(isDark),
    series: [
      {
        type: 'pie',
        radius: donut ? ['48%', '72%'] : ['0%', '74%'],
        center: ['50%', '46%'],
        avoidLabelOverlap: true,
        minShowLabelAngle: 6,
        itemStyle: {
          borderColor: isDark ? '#1f2937' : '#ffffff',
          borderWidth: 1.5,
        },
        label: showLabels
          ? {
              show: true,
              color: textColor(isDark),
              fontSize: 10,
              lineHeight: 12,
              formatter: '{b}\n{d}%',
              overflow: 'truncate',
              width: 78,
            }
          : {show: false},
        labelLine: showLabels
          ? {length: 6, length2: 6, lineStyle: {color: mutedColor(isDark)}}
          : {show: false},
        emphasis: {scale: true, scaleSize: 4},
        data: slices,
      },
    ],
  };

  if (donut && centerLabel) {
    opt.title = {
      text: String(total),
      subtext: centerLabel,
      left: 'center',
      top: '38%',
      textAlign: 'center',
      textStyle: {color: textColor(isDark), fontSize: 16, fontWeight: 'bold'},
      subtextStyle: {color: mutedColor(isDark), fontSize: 10},
    };
  }

  return opt;
};

// Radial bar (polar) chart: every category gets its own angular lane and the bar
// length is log-scaled, so a dominant value can't crush tiny ones into the axis.
// Labels show the full category name plus its true percentage.
export const radialBarOption = ({data = [], isDark = false, logScale = true}) => {
  const cleaned = (data || []).filter(
    d => d && d.name != null && Number(d.value) > 0,
  );
  if (cleaned.length === 0) {
    return {
      tooltip: {show: false},
      angleAxis: {show: false},
      radiusAxis: {show: false},
      series: [],
    };
  }

  const total = cleaned.reduce((sum, d) => sum + Number(d.value), 0);
  const values = cleaned.map(d => Number(d.value));
  const maxV = Math.max(...values);
  const minV = Math.min(...values);
  // Map the smallest value to a positive baseline so its bar stays visible.
  const shift = logScale ? 0.5 - Math.log10(minV) : 0;
  const scale = v => (logScale ? Math.log10(v) + shift : v);
  const maxScale = scale(maxV);

  const pctByName = new Map(
    cleaned.map(d => {
      const p = total > 0 ? (Number(d.value) / total) * 100 : 0;
      return [d.name, `${p < 1 ? p.toFixed(2) : p.toFixed(1)}%`];
    }),
  );

  return {
    tooltip: {
      trigger: 'item',
      formatter: p =>
        `${p.name}: ${Number(p.data.real || 0).toLocaleString()} (${pctByName.get(p.name) || ''})`,
      ...tooltipStyle(isDark),
    },
    angleAxis: {
      type: 'category',
      data: cleaned.map(d => d.name),
      startAngle: 90,
      axisLine: {lineStyle: {color: gridColor(isDark)}},
      axisTick: {show: false},
      axisLabel: {
        color: textColor(isDark),
        fontSize: 10,
        interval: 0,
        margin: 6,
        formatter: name => `${name}\n${pctByName.get(name) || ''}`,
      },
      splitLine: {show: false},
    },
    radiusAxis: {
      type: 'value',
      min: 0,
      max: maxScale,
      axisLine: {show: false},
      axisTick: {show: false},
      axisLabel: {show: false},
      splitLine: {show: false},
    },
    polar: {radius: ['30%', '72%'], center: ['50%', '50%']},
    series: [
      {
        type: 'bar',
        coordinateSystem: 'polar',
        roundCap: true,
        barCategoryGap: '40%',
        data: cleaned.map((d, i) => ({
          value: scale(Number(d.value)),
          name: d.name,
          real: Number(d.value),
          itemStyle: {color: CHART_PALETTE[i % CHART_PALETTE.length]},
        })),
        emphasis: {focus: 'self'},
      },
    ],
  };
};

// Single-series area trend with value labels and a day-over-day tooltip.
// `data` is the raw [{label, count}] counts; the formatter compares to the
// previous point (the previous period isn't fetched for these 7-day trends).
export const trendOption = ({
  labels = [],
  data = [],
  isDark = false,
  color,
  valueFormatter = v => v,
  unit = '',
}) => {
  const seriesColor = color || CHART_PALETTE[0];
  const fmt = v => `${valueFormatter(v)}${unit}`;
  return {
    color: [seriesColor],
    tooltip: {
      trigger: 'axis',
      axisPointer: {type: 'line', lineStyle: {color: mutedColor(isDark)}},
      formatter: params => {
        const p = params?.[0];
        if (!p) return '';
        const i = p.dataIndex;
        const cur = Number(data[i]) || 0;
        const prev = i > 0 ? Number(data[i - 1]) || 0 : null;
        let delta = '';
        if (prev != null) {
          if (prev === 0) {
            delta = cur > 0
              ? `<div style="font-size:10px;color:#22C55E">new vs prev day</div>`
              : `<div style="font-size:10px;color:${mutedColor(isDark)}">— vs prev day</div>`;
          } else {
            const d = ((cur - prev) / prev) * 100;
            const up = d >= 0;
            delta = `<div style="font-size:10px;color:${up ? '#22C55E' : '#EF4444'}">${up ? '+' : ''}${d.toFixed(1)}% vs prev day</div>`;
          }
        }
        return `<div style="font-weight:600;margin-bottom:2px">${p.name}</div>Value: <b>${fmt(cur)}</b>${delta}`;
      },
      ...tooltipStyle(isDark),
    },
    grid: {left: 6, right: 16, top: 24, bottom: 8, containLabel: true},
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: labels,
      ...axisCommon(isDark),
      axisLine: {show: false},
      axisLabel: {color: mutedColor(isDark), fontSize: 10, hideOverlap: true},
    },
    yAxis: {type: 'value', minInterval: 1, ...axisCommon(isDark)},
    series: [
      {
        name: 'Value',
        type: 'line',
        data,
        smooth: 0.35,
        symbolSize: 6,
        showSymbol: true,
        lineStyle: {width: 2, color: seriesColor},
        itemStyle: {color: seriesColor},
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              {offset: 0, color: toRgba(seriesColor, 0.35)},
              {offset: 1, color: toRgba(seriesColor, 0.02)},
            ],
          },
        },
        label: {
          show: labels.length <= 10,
          position: 'top',
          color: textColor(isDark),
          fontSize: 10,
          formatter: p => valueFormatter(p.value),
        },
        emphasis: {focus: 'series'},
      },
    ],
  };
};
